import React from 'react';
import { CalendarIcon, DollarSignIcon, StarIcon, MapPinIcon, ClockIcon, UsersIcon, ShipIcon, HomeIcon, GlobeIcon, LayoutIcon, ThermometerIcon } from 'lucide-react';
import { getDestinationWeather } from '../../data/weatherData';
interface ProductCardProps {
  product: {
    id: number;
    title: string;
    destination: string;
    duration: string;
    price: number;
    image: string;
    category: string;
    rating: number;
    featured: boolean;
    departures: number;
  };
  viewMode: string;
  onClick: () => void;
  onCompareClick: (e: React.MouseEvent, productId: number) => void;
  isInComparison: boolean;
}
export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  viewMode,
  onClick,
  onCompareClick,
  isInComparison
}) => {
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'git':
        return <UsersIcon className="h-4 w-4 text-blue-500" data-id="element-2834" />;
      case 'fit':
        return <GlobeIcon className="h-4 w-4 text-purple-500" data-id="element-2835" />;
      case 'cruises':
        return <ShipIcon className="h-4 w-4 text-teal-500" data-id="element-2836" />;
      case 'hotels':
        return <HomeIcon className="h-4 w-4 text-amber-500" data-id="element-2837" />;
      default:
        return <GlobeIcon className="h-4 w-4 text-gray-500" data-id="element-2838" />;
    }
  };
  // Get weather data for this destination
  const weatherData = getDestinationWeather(product.destination);
  const currentMonth = new Date().toLocaleString('default', {
    month: 'short'
  });
  const currentWeather = weatherData?.months.find(m => m.month === currentMonth);
  // Calculate weather suitability
  const isPerfectWeather = weatherData?.bestTimeToVisit.includes(currentMonth);
  const weatherScore = isPerfectWeather ? weatherData?.climateScore : null;
  if (viewMode === 'list') {
    return <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer" onClick={onClick} data-id="element-2839">
        <div className="flex" data-id="element-2840">
          <div className="w-48 h-32 flex-shrink-0 relative" data-id="element-2841">
            <img src={product.image} alt={product.title} className="w-full h-full object-cover" data-id="element-2842" />
            {/* Weather indicator */}
            {currentWeather && <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center text-xs shadow-sm" data-id="element-2843">
                {currentWeather.icon}
                <span className="ml-1 font-medium" data-id="element-2844">
                  {currentWeather.tempRange}
                </span>
              </div>}
          </div>
          <div className="flex-1 p-4" data-id="element-2845">
            <div className="flex justify-between items-start" data-id="element-2846">
              <div data-id="element-2847">
                <div className="flex items-center" data-id="element-2848">
                  {getCategoryIcon(product.category)}
                  <span className="text-xs text-gray-500 ml-1 capitalize" data-id="element-2849">
                    {product.category === 'git' ? 'Group Tour' : product.category === 'fit' ? 'Custom Tour' : product.category}
                  </span>
                  {product.featured && <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full" data-id="element-2850">
                      Featured
                    </span>}
                  {/* Weather suitability badge */}
                  {isPerfectWeather && <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full flex items-center" data-id="element-2851">
                      <ThermometerIcon className="h-3 w-3 mr-1" data-id="element-2852" />
                      Perfect Weather Now
                    </span>}
                </div>
                <h3 className="text-lg font-bold text-gray-800 mt-1" data-id="element-2853">
                  {product.title}
                </h3>
                <div className="flex items-center mt-1" data-id="element-2854">
                  <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-2855" />
                  <span className="text-sm text-gray-600" data-id="element-2856">
                    {product.destination}
                  </span>
                </div>
              </div>
              <div className="text-right" data-id="element-2857">
                <div className="flex items-center justify-end" data-id="element-2858">
                  <StarIcon className="h-4 w-4 text-yellow-400 mr-1" data-id="element-2859" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-2860">
                    {product.rating}
                  </span>
                </div>
                <p className="text-xl font-bold text-blue-600 mt-1" data-id="element-2861">
                  ${product.price}
                </p>
                <p className="text-xs text-gray-500" data-id="element-2862">per person</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4" data-id="element-2863">
              <div className="flex space-x-4" data-id="element-2864">
                <div className="flex items-center" data-id="element-2865">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-2866" />
                  <span className="text-sm text-gray-600" data-id="element-2867">
                    {product.duration}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2868">
                  <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-2869" />
                  <span className="text-sm text-gray-600" data-id="element-2870">
                    {product.departures} departures
                  </span>
                </div>
                {weatherScore && <div className="flex items-center" data-id="element-2871">
                    <ThermometerIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-2872" />
                    <span className="text-sm text-green-600" data-id="element-2873">
                      {weatherScore}% climate match
                    </span>
                  </div>}
              </div>
              <div className="flex space-x-2" data-id="element-2874">
                <button className={`px-3 py-1.5 text-sm font-medium rounded-md border flex items-center ${isInComparison ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`} onClick={e => {
                e.stopPropagation();
                onCompareClick(e, product.id);
              }} data-id="element-2875">
                  <LayoutIcon className="h-4 w-4 mr-1" data-id="element-2876" />
                  {isInComparison ? 'Added' : 'Compare'}
                </button>
                <button className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-2877">
                  View Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer relative" onClick={onClick} data-id="element-2878">
      <div className="relative" data-id="element-2879">
        <img src={product.image} alt={product.title} className="w-full h-48 object-cover" data-id="element-2880" />
        {product.featured && <div className="absolute top-2 left-2 px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded" data-id="element-2881">
            Featured
          </div>}
        <div className="absolute top-2 right-2 flex items-center bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded" data-id="element-2882">
          <StarIcon className="h-3 w-3 mr-1" data-id="element-2883" />
          {product.rating}
        </div>
        {/* Weather indicator */}
        {currentWeather && <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center shadow-sm" data-id="element-2884">
            {currentWeather.icon}
            <span className="text-xs font-medium ml-1" data-id="element-2885">
              {currentWeather.tempRange}
            </span>
          </div>}
        {/* Perfect weather badge */}
        {isPerfectWeather && <div className="absolute bottom-2 right-2 bg-green-500/90 text-white backdrop-blur-sm rounded-full px-2 py-1 text-xs font-medium flex items-center shadow-sm" data-id="element-2886">
            <ThermometerIcon className="h-3 w-3 mr-1" data-id="element-2887" />
            Perfect Weather
          </div>}
      </div>
      <div className="p-4" data-id="element-2888">
        <div className="flex items-center" data-id="element-2889">
          {getCategoryIcon(product.category)}
          <span className="text-xs text-gray-500 ml-1 capitalize" data-id="element-2890">
            {product.category === 'git' ? 'Group Tour' : product.category === 'fit' ? 'Custom Tour' : product.category}
          </span>
        </div>
        <h3 className="text-lg font-bold text-gray-800 mt-1 line-clamp-1" data-id="element-2891">
          {product.title}
        </h3>
        <div className="flex items-center mt-1" data-id="element-2892">
          <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-2893" />
          <span className="text-sm text-gray-600" data-id="element-2894">{product.destination}</span>
        </div>
        <div className="flex items-center justify-between mt-2" data-id="element-2895">
          <div className="flex items-center" data-id="element-2896">
            <ClockIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-2897" />
            <span className="text-sm text-gray-600" data-id="element-2898">{product.duration}</span>
          </div>
          <div className="flex items-center" data-id="element-2899">
            <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-2900" />
            <span className="text-sm text-gray-600" data-id="element-2901">
              {product.departures} departures
            </span>
          </div>
        </div>
        {weatherScore && <div className="mt-2 flex items-center text-sm text-green-600" data-id="element-2902">
            <ThermometerIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-2903" />
            <span data-id="element-2904">{weatherScore}% climate match</span>
          </div>}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100" data-id="element-2905">
          <div data-id="element-2906">
            <p className="text-lg font-bold text-blue-600" data-id="element-2907">${product.price}</p>
            <p className="text-xs text-gray-500" data-id="element-2908">per person</p>
          </div>
          <div className="flex space-x-2" data-id="element-2909">
            <button className={`p-1.5 rounded-md border ${isInComparison ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`} onClick={e => {
            e.stopPropagation();
            onCompareClick(e, product.id);
          }} title={isInComparison ? 'Added to comparison' : 'Add to comparison'} data-id="element-2910">
              <LayoutIcon className="h-4 w-4" data-id="element-2911" />
            </button>
            <button className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-2912">
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>;
};